import { Component } from '@theme/component'
import { playStoryVideo, STORY } from '@theme/story-config'
import { StoryOverlayController } from '@theme/story-overlay'

class StoryComponent extends Component {
  storyHoverController = null
  storyHoverIndex = null
  storyMotionQuery = null
  storyPreviewIndex = 0
  storyPreviewItems = []
  storyPreviewTimer = null
  storyRail = null
  storyResizeObserver = null

  /** @type {StoryOverlayController | null} */
  overlay = null

  connectedCallback() {
    super.connectedCallback()
    this.overlay = new StoryOverlayController(this)
    this.initStoryRail()
    this.initStoryPreview()
    document.addEventListener(
      'visibilitychange',
      this.handleStoryVisibilityChange
    )
  }

  openStoryPanel(event) {
    event.preventDefault()
    if (!(event.target instanceof HTMLButtonElement)) {
      return
    }
    this.overlay?.openStoryButton(event.target)
  }

  disconnectedCallback() {
    this.overlay?.destroy()
    this.overlay = null
    this.teardownStoryPreview()
    this.teardownStoryRail()
    document.removeEventListener(
      'visibilitychange',
      this.handleStoryVisibilityChange
    )
    super.disconnectedCallback()
  }

  initStoryRail() {
    this.storyRail = this.querySelector(STORY.selectors.rail)
    if (!(this.storyRail instanceof HTMLElement)) {
      this.storyRail = null
      return
    }
    this.updateStoryOverflowState()
    this.storyResizeObserver = new ResizeObserver(() =>
      this.updateStoryOverflowState()
    )
    this.storyResizeObserver.observe(this.storyRail)
    for (const item of this.storyRail.children) {
      this.storyResizeObserver.observe(item)
    }
  }

  updateStoryOverflowState() {
    if (!this.storyRail) {
      return
    }
    const overflows =
      this.storyRail.scrollWidth > this.storyRail.clientWidth + 1
    this.storyRail.dataset.overflow = overflows ? 'true' : 'false'
  }

  teardownStoryRail() {
    this.storyResizeObserver?.disconnect()
    this.storyResizeObserver = null
    this.storyRail = null
  }

  initStoryPreview() {
    this.storyPreviewItems = Array.from(
      this.querySelectorAll(STORY.selectors.item)
    )
    if (!this.storyPreviewItems.length) {
      return
    }
    this.storyMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.storyMotionQuery.addEventListener(
      'change',
      this.handleStoryMotionChange
    )
    this.bindStoryHover()
    this.activateStoryPreviewAt(0)
    if (!this.storyMotionQuery.matches) {
      this.startStoryPreview()
    } else {
      this.pauseStoryPreviewVideos()
    }
  }

  bindStoryHover() {
    if (!window.matchMedia('(hover: hover)').matches) {
      return
    }
    this.storyHoverController = new AbortController()
    const { signal } = this.storyHoverController
    for (let i = 0; i < this.storyPreviewItems.length; i++) {
      const item = this.storyPreviewItems[i]
      item.addEventListener('mouseenter', () => this.onStoryHoverEnter(i), {
        signal
      })
      item.addEventListener('mouseleave', () => this.onStoryHoverLeave(i), {
        signal
      })
    }
  }

  onStoryHoverEnter(index) {
    if (this.overlay?.isOpen || this.storyMotionQuery?.matches) {
      return
    }
    this.storyHoverIndex = index
    this.activateStoryPreviewAt(index)
    this.startStoryPreview()
  }

  onStoryHoverLeave(index) {
    if (this.storyHoverIndex !== index) {
      return
    }
    this.storyHoverIndex = null
  }

  teardownStoryPreview() {
    this.stopStoryPreview()
    this.pauseStoryPreviewVideos()
    this.storyHoverController?.abort()
    this.storyHoverController = null
    this.storyHoverIndex = null
    this.storyMotionQuery?.removeEventListener(
      'change',
      this.handleStoryMotionChange
    )
    this.storyMotionQuery = null
    this.storyPreviewItems = []
    this.storyPreviewIndex = 0
  }

  startStoryPreview() {
    if (!this.storyPreviewItems.length) {
      return
    }
    if (this.overlay?.isOpen || this.storyMotionQuery?.matches) {
      return
    }
    this.stopStoryPreview()
    this.storyPreviewTimer = window.setInterval(
      () => this.advanceStoryPreview(),
      STORY.delays.preview
    )
  }

  stopStoryPreview() {
    if (!this.storyPreviewTimer) {
      return
    }
    window.clearInterval(this.storyPreviewTimer)
    this.storyPreviewTimer = null
  }

  advanceStoryPreview() {
    if (!this.storyPreviewItems.length) {
      return
    }
    if (this.storyHoverIndex !== null) {
      this.activateStoryPreviewAt(this.storyHoverIndex)
      return
    }
    const nextIndex =
      this.storyPreviewItems.length === 1
        ? 0
        : (this.storyPreviewIndex + 1) % this.storyPreviewItems.length
    this.activateStoryPreviewAt(nextIndex)
  }

  activateStoryPreviewAt(index) {
    this.storyPreviewIndex = index
    for (let i = 0; i < this.storyPreviewItems.length; i++) {
      const video = this.storyPreviewItems[i].querySelector(
        STORY.selectors.sourceVideo
      )
      if (!(video instanceof HTMLVideoElement)) {
        continue
      }
      if (i === index) {
        video.currentTime = 0
        playStoryVideo(video)
      } else {
        video.pause()
      }
    }
  }

  pauseStoryPreviewVideos() {
    for (const item of this.storyPreviewItems) {
      const video = item.querySelector(STORY.selectors.sourceVideo)
      if (video instanceof HTMLVideoElement) {
        video.pause()
      }
    }
  }

  pausePreviewForOverlay() {
    this.stopStoryPreview()
    this.pauseStoryPreviewVideos()
    this.storyHoverIndex = null
  }

  resumePreviewAfterOverlay() {
    if (
      document.hidden ||
      this.storyMotionQuery?.matches ||
      !this.storyPreviewItems.length
    ) {
      return
    }
    this.activateStoryPreviewAt(this.storyPreviewIndex)
    this.startStoryPreview()
  }

  handleStoryVisibilityChange = () => {
    if (document.hidden) {
      this.stopStoryPreview()
      this.pauseStoryPreviewVideos()
      this.storyHoverIndex = null
      return
    }
    if (this.overlay?.isOpen || this.storyMotionQuery?.matches) {
      return
    }
    this.activateStoryPreviewAt(this.storyPreviewIndex)
    this.startStoryPreview()
  }

  handleStoryMotionChange = () => {
    if (this.storyMotionQuery?.matches) {
      this.stopStoryPreview()
      this.pauseStoryPreviewVideos()
      this.storyHoverIndex = null
      return
    }
    if (this.overlay?.isOpen) {
      return
    }
    this.activateStoryPreviewAt(this.storyPreviewIndex)
    this.startStoryPreview()
  }
}

if (!customElements.get('story-component')) {
  customElements.define('story-component', StoryComponent)
}
